<template>
  <modal
    name="subscription-info-modal"
    classes="subscription-info-modal"
    height="auto"
    @opened="loadElements"
    @closed="clearState"
  >
    <button
      class="close-btn"
      @click="$emit('close')"
    >
      <icon
        name="cross"
        variant="a1a1a1"
      />
    </button>
    <p class="title">
      Update Credit Card
    </p>
    <label for="cardholder-name">Cardholder Name</label>
    <input
      id="cardholder-name"
      v-model="name"
      type="text"
    >
    <label for="stripe-elements">Payment Information</label>
    <div id="stripe-elements" />
    <div
      v-if="errorMessage.length > 0"
      class="errors"
    >
      <icon
        name="warning"
        variant="dc3416"
      />
      <p>{{ errorMessage }}</p>
    </div>
    <button
      class="submit-btn"
      :disabled="submitting"
      @click="handleSubmission"
    >
      Save
    </button>
    <button
      class="cancel-btn"
      :disabled="submitting"
      @click="$emit('close')"
    >
      Cancel
    </button>
  </modal>
</template>

<script>
import * as Sentry from '@sentry/vue';

import Icon from '@components/singletons/icon';
import ProfileMixin from '@/mixins/profile';

let card;
let stripe;

export default {
  components: { Icon },
  mixins: [ProfileMixin],
  emits: ['close', 'set-cards'],
  data() {
    return {
      errorMessage: '',
      name: '',
      submitting: false,
    };
  },
  methods: {
    clearState() {
      this.errorMessage = '';
      this.name = '';
      this.submitting = false;
    },
    handleSubmission() {
      if (this.name.length < 1) {
        this.errorMessage = 'Your name cannot be blank.';
        return;
      }
      this.submitting = true;
      this.$analytics.events.savedCreditCard();
      stripe.createSource(card, { owner: { name: this.name }}).then(resp => {
        if (resp.error) {
          if (resp.error.type === 'validation_error') {
            this.errorMessage = resp.error.message;
          } else {
            // Won't actually throw an error but we should make sure it gets sent to Sentry
            Sentry.captureMessage(JSON.stringify(resp.error));
            this.errorMessage = 'Uh-oh! Something went wrong. Contact support if this continues.';
          }
          this.submitting = false;
        } else {
          let notification;
          this.addCard(resp.source.id)
            .then((cards) => {
              this.$emit('set-cards', cards);
              notification = {
                group: 'notifications',
                text: 'Payment method updated successfully',
                type: 'success',
              };
            }).catch(() => {
              notification = {
                group: 'notifications',
                text: 'Error occurred when saving payment method. Contact support if this continues',
                type: 'error',
              };
            }).finally(() => {
              this.submitting = false;
              this.$emit('close');
              this.$notify(notification);
            });
      }
      }).catch(() => {
        this.errorMessage = 'Uh-oh! Something went wrong. Contact support if this continues.';
        this.submitting = false;
      });
    },
    loadElements() {
      if (window.Stripe) {
        stripe = window.Stripe(process.env.STRIPE_PUBLISHABLE_KEY);
        const elements = stripe.elements();

        card = elements.create('card');
        card.mount('#stripe-elements');
        card.on('change', (event) => {
          if (event.error) {
            this.errorMessage = event.error.message;
          } else {
            this.errorMessage = '';
          }
        });
      } else {
        Sentry.captureMessage('Stripe Elements failed to load: missing Stripe on window');
      }
    },
  },
};
</script>

<style lang="scss">
  .subscription-info-modal {
    background-color: white;
    padding: rem-calc(30);
    border-radius: rem-calc(10);
    max-width: 98%;

    @include breakpoint(small) {
      margin-left: 1%;
    }

    .close-btn {
      right: rem-calc(6);
      position: absolute;
      top: rem-calc(6);
      background: transparent;
      border: none;
      padding: 0;
      opacity: 0.4;

      @include breakpoint(small) {
        right: rem-calc(4);
      }

      img {
        height: rem-calc(20);
        width: rem-calc(20);

        @include breakpoint(small) {
          height: rem-calc(24);
          width: rem-calc(24);
        }
      }
    }

    .title {
      font-size: rem-calc(24);
      font-weight: $semi-bold;
      margin: 0 0 rem-calc(32);
    }

    label {
      margin-bottom: rem-calc(10);
      display: block;
      font-weight: normal;
      font-size: rem-calc(14);
    }

    input[type="text"],
    .StripeElement {
      border-radius: rem-calc(4);
      border: 1px solid #d3d3d3;
      font-size: rem-calc(14);
      margin-bottom: rem-calc(27);
    }

    input[type="text"] {
      display: block;
      width: calc(100% - 16px);
      font-weight: $semi-bold;
    }

    .StripeElement {
      padding: rem-calc(10) rem-calc(8) rem-calc(8);
      font-family: inherit;
      height: rem-calc(20);
    }

    .errors {
      padding: rem-calc(10) rem-calc(16);
      background-color: hlt-transparentize(#dc3416, .8);
      color: #dc3416;
      border-radius: rem-calc(4);
      margin-bottom: rem-calc(27);
      display: flex;
      align-items: center;
      justify-content: center;

      p {
        margin: 0 0 0 rem-calc(8);
      }

      img {
        flex-shrink: 0;
        flex-grow: 0;
        height: rem-calc(20);
      }
    }

    .submit-btn:disabled,
    .cancel-btn:disabled {
      opacity: .4;
    }

    .submit-btn {
      font-size: rem-calc(14);
      color: #ffffff;
      border-radius: rem-calc(4);
      background-color: #00d8f8;
      padding: rem-calc(8) rem-calc(28);
    }

    .cancel-btn {
      font-size: rem-calc(14);
      margin-left: rem-calc(30);
      font-weight: $semi-bold;
      background-color: transparent;
      &:hover {
        text-decoration: underline;
      }
    }
  }

</style>
